import { useEffect, useRef, useState } from 'react';
import logoKarmaio from '@/assets/logo.png';

export function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Instant cursor tracking via rAF + transform (no React state per move)
  useEffect(() => {
    let x = 0, y = 0;
    let frame = 0;
    const render = () => {
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      }
      frame = 0;
    };
    const onMove = (e: MouseEvent) => {
      x = e.clientX;
      y = e.clientY;
      setIsVisible(true);
      if (!frame) frame = requestAnimationFrame(render);
    };
    const onEnter = () => setIsVisible(true);
    const onLeave = () => setIsVisible(false);
    const onDown = () => setIsClicking(true);
    const onUp = () => setIsClicking(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Track hoverable elements
  useEffect(() => {
    const apply = () => {
      const hoverables = document.querySelectorAll('a, button, [role="button"], input, textarea, select, .hoverable');
      hoverables.forEach((el) => {
        el.addEventListener('mouseenter', () => setIsHovering(true));
        el.addEventListener('mouseleave', () => setIsHovering(false));
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (typeof window !== 'undefined' && 'ontouchstart' in window) {
    return null;
  }

  const size = isClicking ? 26 : isHovering ? 42 : 34;

  return (
    <>
      <style>{`* { cursor: none !important; }`}</style>
      <div
        ref={cursorRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999]"
        style={{
          width: size,
          height: size,
          opacity: isVisible ? 1 : 0,
          willChange: 'transform',
          transition: 'width 0.12s ease, height 0.12s ease, opacity 0.15s',
        }}
      >
        <img
          src={logoKarmaio}
          alt=""
          aria-hidden="true"
          draggable={false}
          width={150}
          height={170}
          className="w-full h-full object-contain select-none"
          style={{
            filter: `drop-shadow(0 0 8px hsl(8 85% 55% / 0.6))${isHovering ? ' drop-shadow(0 0 14px hsl(8 85% 55% / 0.8))' : ''}`,
          }}
        />
      </div>
    </>
  );
}
